/**
 * Preferência do professor: dashboard legado vs novo.
 * Endpoints: GET/PUT /api/v1/user/dashboard-preferences
 */
import { defineStore } from 'pinia';
import api from '@/api/config/axios';

const BASE = '/api/v1/user';
const SESSION_DISMISS_KEY = 'dashboard_v2_promo_session_dismissed';
const SHELL_HINT_KEY = 'dashboard_v2_shell_hint';

function getErrorMessage(err) {
    if (!err) return 'Erro desconhecido';
    const data = err.response?.data;
    if (data?.message) return data.message;
    if (typeof data?.errors === 'object') {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return err.message || 'Erro ao processar requisição';
}

function readShellHint() {
    try {
        return localStorage.getItem(SHELL_HINT_KEY) === '1';
    } catch {
        return false;
    }
}

function writeShellHint(active) {
    try {
        if (active) localStorage.setItem(SHELL_HINT_KEY, '1');
        else localStorage.removeItem(SHELL_HINT_KEY);
    } catch {
        /* ignore */
    }
}

export const useUserDashboardPreferencesStore = defineStore('userDashboardPreferences', {
    state: () => ({
        loading: false,
        saving: false,
        error: null,
        loaded: false,
        userId: null,
        dashboard: 'legacy',
        hidePromo: false,
        available: false,
        canSwitch: false,
        effectiveDashboard: 'legacy',
        sessionPromoDismissed: false,
        shellHint: readShellHint(),
    }),

    getters: {
        isLoading: (state) => state.loading,
        isSaving: (state) => state.saving,
        getError: (state) => state.error,
        /**
         * Shell v2 ativo. Antes do fetch, usa hint local para evitar flash do Cork.
         * Depois do fetch, backend é a fonte de verdade.
         */
        isNewDashboardActive: (state) => {
            if (state.loaded) {
                return state.available && state.effectiveDashboard === 'new';
            }
            return !!state.shellHint;
        },
        shouldShowTryPromo: (state) =>
            state.loaded &&
            state.available &&
            state.effectiveDashboard === 'legacy' &&
            !state.hidePromo,
        isPromoHiddenPermanently: (state) => !!state.hidePromo,
        isPromoDismissedThisSession: (state) => !!state.sessionPromoDismissed,
    },

    actions: {
        applyPayload(data) {
            const payload = data?.data ?? data ?? {};
            this.userId = payload.user_id ?? this.userId;
            this.dashboard = payload.dashboard === 'new' ? 'new' : 'legacy';
            this.hidePromo = !!payload.hide_promo;
            this.available = !!payload.available;
            this.canSwitch = !!payload.can_switch;
            this.effectiveDashboard =
                payload.effective_dashboard === 'new' && this.available ? 'new' : 'legacy';
            this.loaded = true;
            this.shellHint = this.effectiveDashboard === 'new' && this.available;
            writeShellHint(this.shellHint);
            this.hydrateSessionDismiss();
        },

        hydrateSessionDismiss() {
            try {
                this.sessionPromoDismissed = sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
            } catch {
                this.sessionPromoDismissed = false;
            }
        },

        async fetchPreferences({ force = false } = {}) {
            if (this.loaded && !force) {
                return {
                    data: {
                        dashboard: this.dashboard,
                        hide_promo: this.hidePromo,
                        available: this.available,
                        can_switch: this.canSwitch,
                        effective_dashboard: this.effectiveDashboard,
                    },
                };
            }

            this.loading = true;
            this.error = null;

            try {
                const { data } = await api.get(`${BASE}/dashboard-preferences`);
                this.applyPayload(data);
                return data;
            } catch (err) {
                this.error = getErrorMessage(err);
                this.available = false;
                this.effectiveDashboard = 'legacy';
                this.shellHint = false;
                writeShellHint(false);
                this.loaded = true;
                return null;
            } finally {
                this.loading = false;
            }
        },

        async updatePreferences(payload) {
            this.saving = true;
            this.error = null;

            try {
                const { data } = await api.put(`${BASE}/dashboard-preferences`, payload);
                this.applyPayload(data);
                return data;
            } catch (err) {
                this.error = getErrorMessage(err);
                throw err;
            } finally {
                this.saving = false;
            }
        },

        async setDashboard(dashboard) {
            const next = dashboard === 'new' ? 'new' : 'legacy';
            return this.updatePreferences({ dashboard: next });
        },

        async adoptNewDashboard() {
            return this.setDashboard('new');
        },

        async revertToLegacy() {
            return this.setDashboard('legacy');
        },

        dismissPromoThisSession() {
            this.sessionPromoDismissed = true;
            try {
                sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
            } catch {
                /* ignore */
            }
        },

        async hidePromoPermanently() {
            this.dismissPromoThisSession();
            return this.updatePreferences({ hide_promo: true });
        },

        clearError() {
            this.error = null;
        },

        reset() {
            this.loading = false;
            this.saving = false;
            this.error = null;
            this.loaded = false;
            this.userId = null;
            this.dashboard = 'legacy';
            this.hidePromo = false;
            this.available = false;
            this.canSwitch = false;
            this.effectiveDashboard = 'legacy';
            this.sessionPromoDismissed = false;
            this.shellHint = false;
            writeShellHint(false);
            try {
                sessionStorage.removeItem(SESSION_DISMISS_KEY);
            } catch {
                /* ignore */
            }
        },
    },
});
